(() => {
  'use strict';

  const WIDGET_SELECTOR = '#judgeme_product_reviews[data-product-id]';
  const SHOP_DOMAIN = 'wq1s2c-aj.myshopify.com';
  const FIX_CLASS = 'loopluna-jm-media-fix';

  const normalize = (value) =>
    String(value || '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();

  const reviewKey = (name, rating, body) =>
    [normalize(name), Number(rating) || 0, normalize(body)].join('|');

  const safeJudgeMeUrl = (value) => {
    if (!value) return null;

    try {
      const url = new URL(value, window.location.href);
      const isJudgeMeHost =
        url.hostname === 'judgeme.com' || url.hostname.endsWith('.judgeme.com');

      return url.protocol === 'https:' && isJudgeMeHost ? url.href : null;
    } catch (_error) {
      return null;
    }
  };

  const apiUrl = (page) => {
    const params = new URLSearchParams({
      widget_type: 'review-widget',
      page: String(page),
      review_type: 'shop-reviews',
      require_json: 'true',
      shop_domain: SHOP_DOMAIN,
      platform: 'shopify',
    });

    return `https://api.judge.me/reviews/all_reviews_js_based?${params}`;
  };

  const fetchReviewPage = async (page) => {
    const response = await fetch(apiUrl(page), {
      credentials: 'omit',
      mode: 'cors',
    });

    if (!response.ok) throw new Error(`Judge.me returned ${response.status}`);
    return response.json();
  };

  const loadStoreReviewMedia = async () => {
    const firstPage = await fetchReviewPage(1);
    const totalPages = Math.min(
      Math.max(Number(firstPage.pagination?.total_pages) || 1, 1),
      25,
    );

    const remainingPages = await Promise.all(
      Array.from({ length: totalPages - 1 }, (_item, index) =>
        fetchReviewPage(index + 2),
      ),
    );

    const reviews = [firstPage, ...remainingPages].flatMap(
      (page) => page.reviews || [],
    );
    const candidates = new Map();

    reviews.forEach((review) => {
      if (!review.is_shop_review || !Array.isArray(review.pictures_urls)) return;

      const pictures = review.pictures_urls
        .map((picture) => ({
          original: safeJudgeMeUrl(picture.original || picture.huge),
          thumbnail: safeJudgeMeUrl(
            picture.small || picture.compact || picture.original || picture.huge,
          ),
          retina: safeJudgeMeUrl(picture.huge || picture.original),
        }))
        .filter((picture) => picture.original && picture.thumbnail);

      if (pictures.length === 0) return;

      const key = reviewKey(review.reviewer_name, review.rating, review.body);
      const matches = candidates.get(key) || [];
      matches.push(pictures);
      candidates.set(key, matches);
    });

    const uniqueMatches = new Map();
    candidates.forEach((matches, key) => {
      if (matches.length === 1) uniqueMatches.set(key, matches[0]);
    });

    return uniqueMatches;
  };

  const addStyles = () => {
    if (document.getElementById(`${FIX_CLASS}-styles`)) return;

    const style = document.createElement('style');
    style.id = `${FIX_CLASS}-styles`;
    style.textContent = `
      .${FIX_CLASS} {
        display: flex;
        flex-wrap: wrap;
        gap: var(--jm-space-200, 8px);
      }

      .${FIX_CLASS}__link {
        display: block;
        width: 96px;
        max-width: 26vw;
        aspect-ratio: 1 / 1;
        overflow: hidden;
        border-radius: var(--jm-widget-border-radius, 8px);
        background: #f5f5f5;
        cursor: zoom-in;
      }

      .${FIX_CLASS}__image {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    `;
    document.head.append(style);
  };

  const cardKey = (card) => {
    const name = card.querySelector('.jm-reviewer-info__name')?.textContent;
    const body = card.querySelector(
      '.jdgm-review-content__body-content',
    )?.textContent;
    const ratingLabel = card
      .querySelector('.jm-star-rating')
      ?.getAttribute('aria-label');
    const rating = Number.parseFloat(ratingLabel || '0');

    return reviewKey(name, rating, body);
  };

  const appendPictures = (card, pictures) => {
    const row = document.createElement('div');
    row.className = FIX_CLASS;
    row.setAttribute('data-loopluna-review-media', 'matched-store-review');

    pictures.forEach((picture) => {
      const link = document.createElement('a');
      link.className = `${FIX_CLASS}__link`;
      link.href = picture.original;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.setAttribute('aria-label', 'View review image');

      const image = document.createElement('img');
      image.className = `${FIX_CLASS}__image`;
      image.src = picture.thumbnail;
      if (picture.retina) {
        image.srcset = `${picture.thumbnail} 1x, ${picture.retina} 2x`;
      }
      image.alt = 'Review Image';
      image.loading = 'lazy';

      link.append(image);
      row.append(link);
    });

    const content = card.querySelector(':scope > .jm-stack') || card;
    content.append(row);
  };

  const boot = () => {
    const widget = document.querySelector(WIDGET_SELECTOR);
    if (!widget || widget.dataset.looplunaMediaFixReady === 'true') return Boolean(widget);

    widget.dataset.looplunaMediaFixReady = 'true';
    addStyles();

    loadStoreReviewMedia()
      .then((mediaByReview) => {
        let scheduled = false;

        const syncProductReviews = () => {
          scheduled = false;

          const productTab = widget.querySelector('#jdgm-tab_product_reviews');
          if (productTab && productTab.getAttribute('aria-selected') !== 'true') return;

          widget
            .querySelectorAll('.jm-review-item[review-uuid]')
            .forEach((card) => {
              if (card.querySelector(`.${FIX_CLASS}`)) return;
              if (card.querySelector('.jm-media-thumbnail-list__thumbnail')) return;

              const pictures = mediaByReview.get(cardKey(card));
              if (pictures?.length) appendPictures(card, pictures);
            });
        };

        const scheduleSync = () => {
          if (scheduled) return;
          scheduled = true;
          window.requestAnimationFrame(syncProductReviews);
        };

        const observer = new MutationObserver(scheduleSync);
        observer.observe(widget, {
          attributes: true,
          attributeFilter: ['aria-selected'],
          childList: true,
          subtree: true,
        });

        syncProductReviews();
      })
      .catch((error) => {
        console.warn('Loopluna Judge.me media repair could not load:', error);
      });

    return true;
  };

  const start = () => {
    if (boot()) return;

    const waiter = new MutationObserver(() => {
      if (boot()) waiter.disconnect();
    });
    waiter.observe(document.documentElement, { childList: true, subtree: true });
    window.setTimeout(() => waiter.disconnect(), 30000);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
